/**
 * T17-04, corrected by T18-02 — where a block of the sidecar map sits in the script as it stands NOW.
 *
 * The sidecar map is built by the decompiler, so every line number in it is a line number of the
 * text the decompiler produced. The margin, the highlight and the caret all render the text the
 * writer is typing. The two agree until the writer adds or removes a line, and then they do not:
 * one blank line at the top moves every line of the script down by one and moves nothing in the map,
 * so the first scene's labels stay on the blank line and every later label is one row out.
 *
 * This module is the join. It aligns the two texts and answers two questions, both of which can
 * answer "I don't know":
 *
 *  * `current(baselineLine)` — which row of the script this block is beside now;
 *  * `baseline(currentLine)` — which block of the map this row of the script came from.
 *
 * **Uncertainty is a real answer.** Where nothing in the text says which of several rows an old line
 * became, the answer is null and the caller withholds its label rather than putting it beside a line
 * it may not belong to. A wrong label is worse than no label: the margin is a statement of fact
 * about the writer's episode.
 *
 * ### What gives a line its identity (T18-02)
 *
 * The first version matched the common prefix of the two texts line by line and then mapped any
 * unmatched run of equal length by position. Both rules read sameness of TEXT as sameness of LINE,
 * and a script is full of lines whose text repeats: every speaker label, every blank line. Typing
 * `LILLY` and a new line of dialogue immediately above an existing `LILLY` dialogue made the new
 * label match the old one by prefix, and the old block's picture, music and sound labels moved on to
 * a line the writer had just written.
 *
 * Identity here now comes from two things together, and never from text alone:
 *
 * 1. **Content that is its own.** A line whose exact text appears ONCE on each side of the region
 *    being aligned is an anchor: there is no second candidate for it to be confused with. Repeated
 *    lines — labels, blanks, stock directions — are never anchors, so they can never claim identity
 *    by looking like each other.
 * 2. **The block it sits in.** Anchors are taken in order, and everything between two anchors is
 *    aligned against what lies between the SAME two anchors on the other side. The agreed lines
 *    above and below a line are its block context: a scene heading, a speaker label, the dialogue
 *    around it. A line can only ever be matched inside the same surrounded region.
 *
 * Inside a region with no anchors left, exactly one case is answered and everything else is
 * withheld: ONE line on each side, of the same structural kind — the writer rewrote that line where
 * it stood, and the agreed lines on both sides of it leave no second candidate. Two lines against
 * two, or one against three, are ambiguous, and ambiguity is answered with null.
 *
 * ### A speaker label is read with its dialogue (T19-01)
 *
 * Uniqueness inside a region is not uniqueness. A script that says `LILLY` forty times says it once
 * inside any small enough region, and the anchors around a region are what make it small. Moving one
 * `LILLY` block past another left one `LILLY` on each side of a two-line region, that lone copy was
 * read as identity, and the dialogue that had crossed underneath it was read as a rewrite in place.
 * Both speakers kept their old rows while their words changed places, so each block's sound arrived
 * beside the other block's dialogue.
 *
 * Two more things give a line its identity, and together they are what a block is:
 *
 * 3. **A line that still stands somewhere else did not change where it stood.** The one-line rewrite
 *    is withheld when the old line's exact text is still somewhere in the script, or the script
 *    line's exact text is still somewhere in the old text. That line MOVED, and which of the rows
 *    holding that text it is now is not something the text says.
 * 4. **A repeated name is spoken for by its block, never by itself.** Where a label whose name
 *    appears more than once is placed but the content line under it did not go with it, the label is
 *    withheld: the name alone never carried the identity. Where a whole block is left unplaced and
 *    its dialogue is its own line on BOTH sides, the block moved there whole and the label goes with
 *    it. A name that appears once is still its own line and still anchors on its own text.
 *
 * Nothing here parses the script, touches the grammar or reaches the write path. It compares two
 * arrays of strings.
 */

/** The join between the decompiled text's line numbers and the script's. */
export interface LineAlignment {
  /** The 1-based script line a 1-based decompiled line sits on now, or null where unknown. */
  current(baselineLine: number): number | null;
  /** The 1-based decompiled line a 1-based script line came from, or null where unknown. */
  baseline(currentLine: number): number | null;
  /** True when the two texts are the same: every line maps to itself. */
  readonly identical: boolean;
}

const linesOf = (text: string): string[] => String(text ?? '').replace(/\r\n?/g, '\n').split('\n');

/**
 * How deep the anchoring may recurse before a region is left unknown.
 *
 * Every level strictly shrinks the region it is given, so the depth is bounded by the size of the
 * edit; the cap is a guard against a pathological text costing a keystroke, not a rule about
 * scripts. A region abandoned at the cap is withheld, which is the safe answer everywhere here.
 */
const DEPTH_LIMIT = 24;

/** A speaker label, near enough: the shape `example.ts` reads, kept local to avoid a cycle. */
const SPEAKER_SHAPE = /^[A-Z][A-Z0-9 _.-]*?(\s+\([^)]+\))?(\s+\[[a-z]+\])?$/;

/**
 * The structural kind of a line, coarsely: what a reader can see it is at a glance.
 *
 * It is used for one decision only — whether a single line may be treated as a rewrite of the
 * single line that stood where it stands — and a change of kind is a change of thing. A heading
 * where a line of dialogue was is not that dialogue with different words.
 */
function kindOf(line: string): string {
  const spelled = String(line ?? '').trim();

  if (spelled === '') {
    return 'blank';
  }
  if (spelled.startsWith('==')) {
    return 'heading';
  }
  if (spelled.startsWith('*')) {
    return 'answer';
  }
  if (spelled.startsWith('>')) {
    return 'goto';
  }
  if (spelled.startsWith('#')) {
    return 'title';
  }
  if (SPEAKER_SHAPE.test(spelled)) {
    return 'label';
  }

  return 'text';
}

/** How many times each text stands in a whole side. Uniqueness here is a fact about the text. */
function occurrences(lines: readonly string[]): Map<string, number> {
  const counted = new Map<string, number>();

  lines.forEach((line) => counted.set(line, (counted.get(line) ?? 0) + 1));

  return counted;
}

/**
 * The names that repeat, and so can never carry identity on their own.
 *
 * A speaker label is the one line a script repeats on purpose, and repetition is exactly what makes
 * it useless as identity. Anchoring can still put such a name somewhere, because a region small
 * enough holds only one copy of it; what it is placed BY is the block it opens, so every placement
 * of a name on this list has to answer to the content line under it.
 */
function namesThatRepeat(counted: Map<string, number>): Set<string> {
  const repeated = new Set<string>();

  counted.forEach((count, line) => {
    if (count > 1 && kindOf(line) === 'label') {
      repeated.add(line);
    }
  });

  return repeated;
}

/** Which lines of a region carry a text that appears exactly once in it. */
function onlyOnce(lines: readonly string[], from: number, to: number): Map<string, number> {
  const seen = new Map<string, number>();
  const twice = new Set<string>();

  for (let at = from; at < to; at += 1) {
    const line = lines[at];

    if (seen.has(line)) {
      twice.add(line);
    } else {
      seen.set(line, at);
    }
  }
  twice.forEach((line) => seen.delete(line));

  return seen;
}

/**
 * The longest run of anchor pairs that keeps both sides in order.
 *
 * A line that is its own on both sides can still have MOVED, and a moved line is not evidence about
 * anything around it. Taking the longest increasing subsequence keeps the anchors that agree with
 * each other and drops the ones that cross, which are exactly the ones nothing can be built on.
 */
function inOrder(pairs: ReadonlyArray<[number, number]>): Array<[number, number]> {
  const tails: number[] = [];
  const previous: number[] = new Array<number>(pairs.length).fill(-1);
  const at: number[] = [];

  pairs.forEach((pair, index) => {
    const [, place] = pair;
    let low = 0;
    let high = tails.length;

    while (low < high) {
      const middle = Math.floor((low + high) / 2);

      if (tails[middle] < place) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }
    previous[index] = low > 0 ? at[low - 1] : -1;
    at[low] = index;
    tails[low] = place;
  });

  const kept: Array<[number, number]> = [];
  let walk = tails.length > 0 ? at[tails.length - 1] : -1;

  while (walk >= 0) {
    kept.push(pairs[walk]);
    walk = previous[walk];
  }

  return kept.reverse();
}

/**
 * Align the decompiled text against the script as it stands.
 *
 * @param baselineText the text the sidecar map's line numbers belong to
 * @param currentText the text in front of the writer
 */
export function alignLines(baselineText: string, currentText: string): LineAlignment {
  const before = linesOf(baselineText);
  const after = linesOf(currentText);
  const forward: Array<number | null> = new Array<number | null>(before.length).fill(null);
  const backward: Array<number | null> = new Array<number | null>(after.length).fill(null);
  const beforeCount = occurrences(before);
  const afterCount = occurrences(after);
  const repeatedNames = namesThatRepeat(beforeCount);

  const mark = (beforeIndex: number, afterIndex: number): void => {
    forward[beforeIndex] = afterIndex;
    backward[afterIndex] = beforeIndex;
  };

  /** Take a placement back, in both directions, and leave the row free for whatever earned it. */
  const unmark = (beforeIndex: number): void => {
    const afterIndex = forward[beforeIndex];

    if (afterIndex != null) {
      backward[afterIndex] = null;
    }
    forward[beforeIndex] = null;
  };

  /** Align one region of the baseline against the region of the script it is bounded into. */
  const resolve = (
    beforeFrom: number,
    beforeTo: number,
    afterFrom: number,
    afterTo: number,
    depth: number,
  ): void => {
    if (beforeFrom >= beforeTo || afterFrom >= afterTo) {
      return;
    }

    // The region is untouched: same length, same lines, same order. Position is then a fact.
    if (beforeTo - beforeFrom === afterTo - afterFrom) {
      let same = true;

      for (let step = 0; same && step < beforeTo - beforeFrom; step += 1) {
        same = before[beforeFrom + step] === after[afterFrom + step];
      }
      if (same) {
        for (let step = 0; step < beforeTo - beforeFrom; step += 1) {
          mark(beforeFrom + step, afterFrom + step);
        }

        return;
      }
    }
    if (depth >= DEPTH_LIMIT) {
      return;
    }

    // Anchors: a text that is its own on BOTH sides of this region, taken in an order both sides
    // agree on. Nothing repeated can become one, which is what keeps repeated labels out.
    const beforeOnce = onlyOnce(before, beforeFrom, beforeTo);
    const afterOnce = onlyOnce(after, afterFrom, afterTo);
    const pairs: Array<[number, number]> = [];

    beforeOnce.forEach((beforeIndex, line) => {
      const afterIndex = afterOnce.get(line);

      if (afterIndex != null) {
        pairs.push([beforeIndex, afterIndex]);
      }
    });
    pairs.sort((one, other) => one[0] - other[0]);

    const anchors = inOrder(pairs);

    if (anchors.length === 0) {
      // Nothing in this region is its own. One line against one line, of the same kind, is the
      // writer rewriting that line where it stood: the agreed lines on either side leave no other
      // candidate. Anything else has two or more candidates with the same context, and is withheld.
      //
      // T19-01: unless that line is still standing somewhere else. A text the other side still
      // holds was not rewritten here, it MOVED, and which of the rows holding it this one is now is
      // not something either text says.
      const wentElsewhere = afterCount.has(before[beforeFrom]) || beforeCount.has(after[afterFrom]);

      if (beforeTo - beforeFrom === 1
        && afterTo - afterFrom === 1
        && !wentElsewhere
        && kindOf(before[beforeFrom]) === kindOf(after[afterFrom])) {
        mark(beforeFrom, afterFrom);
      }

      return;
    }

    let beforeAt = beforeFrom;
    let afterAt = afterFrom;

    anchors.forEach(([beforeIndex, afterIndex]) => {
      resolve(beforeAt, beforeIndex, afterAt, afterIndex, depth + 1);
      mark(beforeIndex, afterIndex);
      beforeAt = beforeIndex + 1;
      afterAt = afterIndex + 1;
    });
    resolve(beforeAt, beforeTo, afterAt, afterTo, depth + 1);
  };

  resolve(0, before.length, 0, after.length, 0);

  /** The content line a label opens, where it has one. */
  const contentUnder = (index: number): number | null => (
    index + 1 < before.length && kindOf(before[index + 1]) === 'text' ? index + 1 : null
  );

  /**
   * A repeated name keeps a place only while its own dialogue keeps the place under it.
   *
   * This is the check the first version never made. Anchoring inside a small region can put a
   * `LILLY` on a row because it is the only `LILLY` in view, and being the only one in view is not
   * being the only one. Where the content line that belongs to this label did not land directly
   * under the label's row, the block is not there, and the name goes back to saying nothing.
   */
  const holdBlocksTogether = (): void => {
    before.forEach((label, index) => {
      const place = forward[index];
      const content = contentUnder(index);

      if (place == null || content == null || !repeatedNames.has(label)) {
        return;
      }
      if (forward[content] !== place + 1) {
        unmark(index);
      }
    });
  };

  /**
   * The blocks that moved whole, followed by their own content.
   *
   * A block that crossed another one comes out of the regions with neither of its two lines placed.
   * Its dialogue can still say where it went: a line that stands once in the old text and once in
   * the new one has one place it can be, and if the row above that place carries this very name, the
   * block is there and the label goes with it. Anything less than that leaves the label where it is,
   * which is nowhere: dialogue that repeats, dialogue whose place is already claimed by something
   * else, or a different name standing over it.
   */
  const followMovedBlocks = (): void => {
    before.forEach((label, index) => {
      const content = contentUnder(index);

      if (content == null || kindOf(label) !== 'label') {
        return;
      }
      if (forward[index] != null || forward[content] != null) {
        return;
      }
      if (beforeCount.get(before[content]) !== 1 || afterCount.get(before[content]) !== 1) {
        return;
      }
      const went = after.indexOf(before[content]);

      if (went < 1 || after[went - 1] !== label) {
        return;
      }
      if (backward[went] != null || backward[went - 1] != null) {
        return;
      }
      mark(index, went - 1);
      mark(content, went);
    });
  };

  holdBlocksTogether();
  followMovedBlocks();

  const at = (map: Array<number | null>, line: number): number | null => {
    const index = Math.trunc(line) - 1;

    if (!Number.isFinite(line) || index < 0 || index >= map.length) {
      return null;
    }
    const found = map[index];

    return found == null ? null : found + 1;
  };

  return {
    identical: before.length === after.length && before.every((line, index) => line === after[index]),
    current: (line: number) => at(forward, line),
    baseline: (line: number) => at(backward, line),
  };
}

/** The alignment of a text with itself: every line is where it was. Used where there is no edit. */
export const IDENTITY_ALIGNMENT: LineAlignment = Object.freeze({
  identical: true,
  current: (line: number) => (Number.isFinite(line) && line > 0 ? Math.trunc(line) : null),
  baseline: (line: number) => (Number.isFinite(line) && line > 0 ? Math.trunc(line) : null),
});
